import { feedEndpointLabel } from '@/lib/infra/feedEndpointLabel';
import { useMemo } from 'react';
import { useMarketStore } from '../../stores/marketStore';
import type { FeedLogEntry, FeedOwnership } from '../../stores/marketStore';

interface FeedTapeProps {
  onClose: () => void;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
    + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

function shortTicker(ticker: string): string {
  // Canonical game keys (unabated/oddspapi rows): show the team pair.
  if (ticker.includes('|')) {
    const segs = ticker.split('|');
    return segs.slice(-2).join('-');
  }
  if (ticker.startsWith('PM-')) {
    const parts = ticker.split('-');
    return parts.slice(1, 4).join('-');
  }
  const parts = ticker.split('-');
  return parts[parts.length - 1];
}

/** Display name for a feed: the engine identity declared in config (firehose
 *  'OG'; cores/edges via the `label@` prefix in VITE_HYDRA_CORE_FEEDS /
 *  VITE_HYDRA_EDGE_FEEDS — e.g. `hc1`, `he2`).
 *
 *  Fallback for an unlabeled feed is a POSITIONAL SLOT, and is deliberately
 *  spelled so it cannot be mistaken for an instance identity: `he-slot-1`,
 *  `hc-slot-2`, numbered by env-config order from the feed ids `edge:0`,
 *  `core:1`, ….
 *
 *  It must not read as `he1`/`hc1`. Rule 9 reserves that syntax for actual
 *  fleet instances, and this chip is a PROVENANCE surface — the URL behind it
 *  is only visible on hover, so a canonical-looking name here would let an
 *  unverified slot impersonate the real he1 to anyone glancing at the tape.
 *  That is precisely the ambiguity the `label@` mechanism exists to remove, so
 *  the fix is to label the env entry, and the fallback's job is to make its
 *  own ignorance obvious until someone does.
 *
 *  A bare `e<n>` is likewise never generated: that spelling marks the MVP
 *  proof-of-concept generation, which only the deployed e1 is entitled to, and
 *  only via its own env label. */
function feedLabel(feed: FeedOwnership): string {
  if (feed.label) return feed.label;
  if (feed.role === 'firehose') return 'OG';
  if (feed.opOnly === true) return 'op-slot'; // dedicated OP transport — label it in env
  const prefix = feed.role === 'core' ? 'hc' : 'he';
  const idx = Number(feed.id.split(':')[1]);
  return Number.isFinite(idx) ? `${prefix}-slot-${idx + 1}` : `${prefix}-slot-?`;
}

function TypeBadge({ type }: { type: string }) {
  const styles =
    type === 'market' ? 'bg-green-900/40 text-green-400' :
    type === 'consensus' ? 'bg-amber-900/40 text-amber-300' :
    type === 'opOdds' ? 'bg-emerald-900/40 text-emerald-300' :
    'bg-yellow-900/40 text-yellow-400';
  const letter =
    type === 'market' ? 'M' :
    type === 'consensus' ? 'C' :
    type === 'opOdds' ? 'OP' :
    'B';
  return (
    <span className={`inline-flex min-w-4 items-center justify-center rounded px-0.5 text-[9px] ${styles}`}>
      {letter}
    </span>
  );
}

// Consensus values render at tenth-of-a-cent grain when the frame carried
// tenths (hydra ≥2026-07-05) — frames emit on tenth-only moves, so showing
// the integer would make consecutive rows look like duplicate no-ops.
// Falls back to the integer cents on pre-rollout frames. Soccer 3-way
// frames append the draw; the ˢ marks Pinnacle-anchored computes (a
// sharp flip alone emits a frame with zero numeric change).
function ConsensusValue({ entry }: { entry: FeedLogEntry }) {
  const fmt = (tenths: number | undefined, cents: number | undefined) =>
    tenths != null ? (tenths / 10).toFixed(1) : String(cents);
  return (
    <span className="text-foreground/90 shrink-0 tabular-nums">
      {fmt(entry.awayProbTenths, entry.awayProbCents)}
      <span className="text-muted-foreground">/</span>
      {fmt(entry.homeProbTenths, entry.homeProbCents)}
      {entry.drawProbTenths != null && (
        <>
          <span className="text-muted-foreground">/</span>
          {(entry.drawProbTenths / 10).toFixed(1)}
        </>
      )}
      {entry.sharpBookUsed === true && (
        <span className="text-amber-300" title="Pinnacle-anchored (sharpBookUsed)">
          ˢ
        </span>
      )}
    </span>
  );
}

/** Source column: WHICH engine delivered this row (OG / e2 / hc1 …). The
 *  provenance is the point of the tape — it outranks the values, which
 *  truncate first when the row gets tight. */
function SourceTag({ label }: { label?: string }) {
  return (
    <span className="text-foreground/60 bg-muted/40 w-8 shrink-0 truncate rounded px-0.5 text-center text-[8px] font-bold uppercase">
      {label ?? '—'}
    </span>
  );
}

function FeedRow({ entry }: { entry: FeedLogEntry }) {
  // OP snapshot-commit summary row (one per effective commit).
  if (entry.type === 'opOdds' && entry.opSnapshotGames != null) {
    return (
      <div className="border-border/40 hover:bg-muted/40 flex items-center gap-1.5 border-b px-2 py-0.5 font-mono text-[10px]">
        <span className="text-muted-foreground w-[70px] shrink-0">{formatTime(entry.ts)}</span>
        <TypeBadge type={entry.type} />
        <SourceTag label={entry.feedLabel} />
        <span className="text-foreground/80 min-w-0 flex-1 truncate">
          snapshot · {entry.opSnapshotGames} games
        </span>
      </div>
    );
  }
  return (
    <div className="border-border/40 hover:bg-muted/40 flex items-center gap-1.5 border-b px-2 py-0.5 font-mono text-[10px]">
      <span className="text-muted-foreground w-[70px] shrink-0">{formatTime(entry.ts)}</span>
      <TypeBadge type={entry.type} />
      <SourceTag label={entry.feedLabel} />
      <span className="text-muted-foreground w-6 shrink-0 text-center uppercase">{entry.sport}</span>
      <span className="text-foreground/80 min-w-0 flex-1 truncate">{shortTicker(entry.ticker)}</span>
      {entry.type === 'opOdds' && entry.opTombstone === true && (
        <span className="shrink-0 text-red-400" title="no-coverage tombstone (markets: [])">∅</span>
      )}
      {entry.type === 'opOdds' && !entry.opTombstone && entry.awayProbCents != null && entry.homeProbCents != null && (
        <ConsensusValue entry={entry} />
      )}
      {entry.type === 'opOdds' && !entry.opTombstone && entry.bookCount != null && (
        <span className="text-muted-foreground shrink-0">{entry.bookCount}bk</span>
      )}
      {entry.yesBid != null && (
        <span className="text-foreground/90 shrink-0 tabular-nums">
          {entry.yesBid}<span className="text-muted-foreground">/</span>{entry.yesAsk ?? '—'}
        </span>
      )}
      {entry.type === 'market' && (
        <span
          className={`shrink-0 tabular-nums ${entry.volume != null && entry.volume > 0 ? 'text-cyan-400' : 'text-muted-foreground'}`}
          title="Frame's cumulative lifetime traded volume, not rolling 24-hour volume"
        >
          {entry.volume != null ? `v${entry.volume}` : 'v?'}
        </span>
      )}
      {entry.type === 'orderbook' && entry.bookLevels != null && (
        <span className="text-muted-foreground shrink-0">{entry.bookLevels}L</span>
      )}
      {entry.type === 'consensus' && entry.awayProbCents != null && entry.homeProbCents != null && (
        <ConsensusValue entry={entry} />
      )}
    </div>
  );
}

/** One server's chip: `OG MLB SOCCER …` / `c1 CRICKET` / `e2 NFL`. Live
 *  ownership — sports move between chips as the store's arbitration moves
 *  them (a claimant drop/block hands a sport back to OG, where OG carries
 *  it). A disconnected core/edge reads red; claimed-but-not-served sports
 *  render dim (on OG — or dark, for sports OG never carried — until the
 *  engine announces them). Chip color groups the fleet: OG blue, cores
 *  violet, edges cyan. */
/** OddsPapi column sort key: ladder rank when the feed carries one, else the
 *  pre-ladder rule — the designated primary ahead of everything else. */
function opSortKey(feed: FeedOwnership): number {
  if (feed.opRank != null) return feed.opRank;
  return feed.opPreferred === true ? -1 : Number.MAX_SAFE_INTEGER;
}

/** Odds-column channel tag: WHICH odds channel this engine is serving right
 *  now. The merged Odds column shows one chip per engine, so the channel
 *  (unabated consensus vs OddsPapi) moves onto the chip as a tag — `hc1
 *  [OPPI]`, `OG·2 [UNBTD]` — instead of being implied by which column the
 *  chip sits in. Tag colors keep each channel's former column color. */
type OddsChannelTag = 'UNBTD' | 'OPPI';
type ChipFeed = FeedOwnership & { channelTags?: OddsChannelTag[] };

function ServerChip({ feed }: { feed: ChipFeed }) {
  const down = feed.role !== 'firehose' && !feed.connected;
  const badge = down
    ? 'bg-red-900/40 text-red-400'
    : feed.role === 'core'
      ? 'bg-violet-900/40 text-violet-300'
      : feed.role === 'edge'
        ? 'bg-cyan-900/40 text-cyan-300'
        : 'bg-blue-900/40 text-blue-300';
  // Dim ONLY a healthy standby that serves NOTHING in this column — a downed
  // chip stays full-opacity red (a dimmed red chip was nearly invisible,
  // which is the opposite of what a dead primary should look like), and an
  // engine actively serving a channel (OG producing unabated while standing
  // by on the OP ladder) must not read as idle.
  const serving = (feed.channelTags?.length ?? 0) > 0;
  const dim = feed.opStandby === true && !down && !serving;
  // Rank marker on ladder members: the OddsPapi column is a preference order
  // (hc1, hc2, … OG last), and without it two violet core chips are
  // indistinguishable — which one is g1 actually consuming, and which is the
  // backup waiting behind it?
  const rankMark = feed.opRank != null && feed.opRank > 0 ? `·${feed.opRank + 1}` : '';
  // The transport and host identify the upstream without exposing connection
  // credentials from userinfo, paths, query parameters or fragments.
  const rankNote = feed.opRank != null ? ` (rank ${feed.opRank + 1})` : '';
  const endpoint = feed.url ? feedEndpointLabel(feed.url) : null;
  const title = down
    ? `${feedLabel(feed)}${rankNote} disconnected${endpoint ? ` — ${endpoint}` : ''}`
    : feed.opStandby === true
      ? `${feedLabel(feed)}${rankNote} standby${endpoint ? ` — ${endpoint}` : ''}`
      : feed.opRank != null
        ? `${feedLabel(feed)}${rankNote} live${endpoint ? ` — ${endpoint}` : ''}`
        : endpoint ?? undefined;
  return (
    <span
      className={`flex min-w-0 items-center gap-1 ${dim ? 'opacity-50' : ''}`}
      title={title}
    >
      <span className={`shrink-0 rounded px-1 text-[9px] font-bold ${badge}`}>
        {feedLabel(feed)}{rankMark}
      </span>
      {feed.channelTags?.map((tag) => (
        <span
          key={tag}
          className={`shrink-0 rounded px-0.5 text-[8px] font-bold ${
            tag === 'OPPI' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-amber-900/40 text-amber-300'
          }`}
          title={`${feedLabel(feed)} is serving the ${tag === 'OPPI' ? 'OddsPapi' : 'Unabated'} channel`}
        >
          {tag}
        </span>
      ))}
      <span className="text-foreground/70 min-w-0 truncate text-[9px] uppercase">
        {feed.sports.join(' ')}
        {feed.floorSports.length > 0 && (
          // D10 FLOOR: this core is covering these sports with the THIN
          // firehose because no primary serves them — honest degraded
          // state (amber), distinct from real ownership (plain), idle
          // (muted) and dark (red struck).
          <span
            className="text-amber-300/80"
            title={`THIN FLOOR — no primary is serving ${feed.floorSports.join(', ').toUpperCase()}; ${feedLabel(feed)}'s throttled firehose is covering (rule 6). Reassign or restore a primary to clear.`}
            data-floor-sports
          >
            {feed.sports.length > 0 ? ' ' : ''}
            {feed.floorSports.join(' ')}
          </span>
        )}
        {feed.idleClaims.length > 0 && (
          <span className={`text-muted-foreground/60 ${down ? 'line-through' : ''}`}>
            {feed.sports.length > 0 ? ' ' : ''}{feed.idleClaims.join(' ')}
          </span>
        )}
        {feed.darkClaims.length > 0 && (
          // DARK: nobody in the fleet is serving these. Distinct from an idle
          // claim, which still flows from the firehose — here there is no
          // data at all, from anyone. Struck through in red so the tape stops
          // implying some engine has it: attributing a dark sport to OG sent
          // one debugging session at the wrong engine entirely.
          <span
            className="text-red-400/70 line-through"
            title={`no engine is serving ${feed.darkClaims.join(', ').toUpperCase()} — the claimant is not delivering it and OG does not carry it`}
          >
            {feed.sports.length > 0 || feed.idleClaims.length > 0 ? ' ' : ''}
            {feed.darkClaims.join(' ')}
          </span>
        )}
      </span>
    </span>
  );
}

function FeedColumn({
  title,
  color,
  entries,
  servers,
}: {
  title: string;
  color: string;
  entries: FeedLogEntry[];
  servers: ChipFeed[];
}) {
  const marketCount = entries.filter(e => e.type === 'market').length;
  const bookCount = entries.filter(e => e.type === 'orderbook').length;
  const consensusCount = entries.filter(e => e.type === 'consensus').length;
  const opCount = entries.filter(e => e.type === 'opOdds').length;
  // Each provider column only emits its own types (Odds: consensus + opOdds;
  // Kalshi/Poly: market+book) — show the relevant subset so the header stays
  // tight. The Odds column shows both channel counts when both are flowing.
  const summary =
    marketCount === 0 && bookCount === 0 && opCount > 0 && consensusCount > 0
      ? `${consensusCount}C / ${opCount}OP`
      : opCount > 0 && marketCount === 0 && bookCount === 0
        ? `${opCount}OP`
        : consensusCount > 0 && marketCount === 0 && bookCount === 0
          ? `${consensusCount}C`
          : `${marketCount}M / ${bookCount}B`;

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="border-border border-b px-2 py-1.5">
        <div className="flex items-center justify-between">
          <span className={`text-[11px] font-bold ${color}`}>{title}</span>
          <span className="text-muted-foreground text-[10px]">{summary}</span>
        </div>
        {servers.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
            {servers.map((feed) => <ServerChip key={feed.id} feed={feed} />)}
          </div>
        )}
      </div>
      <div className="max-h-72 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="text-muted-foreground px-2 py-3 text-center text-[10px]">Waiting...</div>
        ) : (
          entries.map((entry, i) => <FeedRow key={i} entry={entry} />)
        )}
      </div>
    </div>
  );
}

export function FeedTape({ onClose }: FeedTapeProps) {
  const getFeedLog = useMarketStore((s) => s.getFeedLog);
  const getFeedOwnership = useMarketStore((s) => s.getFeedOwnership);
  const version = useMarketStore((s) => s.version);

  const kalshiEntries = useMemo(() => {
    void version;
    return getFeedLog('kalshi');
  }, [getFeedLog, version]);

  const polyEntries = useMemo(() => {
    void version;
    return getFeedLog('polymarket');
  }, [getFeedLog, version]);

  // Polymarket US (V55). The column exists only while the venue has logged
  // something, so a box whose hydra never emits it renders exactly as before.
  const polyUsEntries = useMemo(() => {
    void version;
    return getFeedLog('polymarket_us');
  }, [getFeedLog, version]);

  // The Odds column carries BOTH odds channels — unabated consensus and
  // OddsPapi — as one stream, newest first. Row badges (C vs OP) and the
  // per-chip channel tags keep the two distinguishable inside the merge.
  const oddsEntries = useMemo(() => {
    void version;
    return [...getFeedLog('unabated'), ...getFeedLog('oddspapi')].sort((a, b) => b.ts - a.ts);
  }, [getFeedLog, version]);

  // Live sport ownership (edge preferred / firehose fallback) for the server
  // chips, resolved PER VENUE: within an announced sport the edge only owns a
  // venue it is actually delivering (e.g. Kalshi toggled off edge-side moves
  // MLB to OG in the Kalshi column while the Poly column keeps it on the edge).
  const kalshiServers = useMemo(() => {
    void version;
    return getFeedOwnership('kalshi');
  }, [getFeedOwnership, version]);

  const polyServers = useMemo(() => {
    void version;
    return getFeedOwnership('polymarket');
  }, [getFeedOwnership, version]);

  const polyUsServers = useMemo(() => {
    void version;
    return getFeedOwnership('polymarket_us');
  }, [getFeedOwnership, version]);

  // The Odds column pins ONE chip per engine involved in either odds channel,
  // with a channel tag for what each is serving RIGHT NOW — `hc1 [OPPI]`,
  // `OG·2 [UNBTD]`. The set is the OP ladder (every OP-capable feed, in
  // LADDER ORDER — hc1, hc2, … OG last — so "which core is live and which is
  // waiting behind it" stays answerable at a glance; a feed with no rank
  // falls back to designated-primary-first so the primary never reads as the
  // backup) merged with the unabated provider (OG — ungated, phasing out).
  // An engine on both channels gets one chip with both facts: OG keeps its
  // ladder rank mark while wearing UNBTD. Only a chip serving NOTHING dims
  // as standby; the ACTIVE OP source is the one wearing OPPI.
  const oddsServers = useMemo(() => {
    const byId = new Map<string, ChipFeed>();
    const ladder = kalshiServers
      .filter((f) => f.opAuthority === true)
      .map<ChipFeed>((f) => ({
        ...f,
        sports: [], idleClaims: [], darkClaims: [], floorSports: [],
        opStandby: f.opActive !== true,
        channelTags: f.opActive === true ? ['OPPI'] : [],
      }))
      .sort((a, b) => opSortKey(a) - opSortKey(b));
    for (const f of ladder) byId.set(f.id, f);
    // The UNBTD tag names the Unabated PROVIDER — the OG firehose. A firehose-
    // role feed that is a full OP source (the gateway) serves no such channel.
    const firehose = kalshiServers.find((f) => f.role === 'firehose' && f.opFullFeed !== true);
    if (firehose) {
      const existing = byId.get(firehose.id);
      if (existing) existing.channelTags = [...(existing.channelTags ?? []), 'UNBTD'];
      else byId.set(firehose.id, { ...firehose, sports: [], idleClaims: [], darkClaims: [], floorSports: [], channelTags: ['UNBTD'] });
    }
    return [...byId.values()];
  }, [kalshiServers]);

  return (
    <div className="bg-background/95 border-border absolute bottom-full right-0 z-50 mb-1 w-[1180px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border shadow-2xl">
      {/* Header */}
      <div className="border-border flex items-center justify-between border-b px-3 py-1.5">
        <span className="text-foreground text-xs font-semibold">Live Feed</span>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground px-1 text-xs"
        >
          ✕
        </button>
      </div>

      {/* Live streams. Market columns skip the dedicated OP transport chip
          (opOnly — it carries no market data); it lives in the Odds column
          only. */}
      <div className="divide-border flex divide-x">
        <FeedColumn title="Kalshi" color="text-blue-400" entries={kalshiEntries} servers={kalshiServers.filter((f) => f.opOnly !== true)} />
        {polyUsEntries.length > 0 && (
          <FeedColumn title="US" color="text-teal-400" entries={polyUsEntries} servers={polyUsServers.filter((f) => f.opOnly !== true)} />
        )}
        <FeedColumn title="Polymarket" color="text-purple-400" entries={polyEntries} servers={polyServers.filter((f) => f.opOnly !== true)} />
        <FeedColumn title="Odds" color="text-emerald-400" entries={oddsEntries} servers={oddsServers} />
      </div>
    </div>
  );
}
